<template>
    <div class="c-table flex-column flex-grow-auto" :class="{'z-border': border, 'z-auto': height == 'auto'}" :id="id" :style="{'height': parsePixel(height)}">
        <div class="c-table-body flex-column flex-grow-auto">
            <div class="c-table-main flex-column flex-grow-auto">
                <c-thead :store="store"></c-thead>
                <c-tbody :store="store" :auto-height="height == 'auto'">
                    <template v-if="$scopedSlots.custom" #custom="{data, index}">
                        <slot name="custom" :data="data" :index="index"></slot>
                    </template>
                </c-tbody>
            </div>

            <div v-show="columnsWidth.left > 1 && scrollXSize" class="c-table-fixed-left flex-column" :style="{'bottom': `${scrollXSize}px`, 'width': `${columnsWidth.left}px`}">
                <c-thead :store="store" fixed="left"></c-thead>
                <c-tbody :store="store" fixed="left"></c-tbody>
            </div>

            <div v-show="columnsWidth.right > 1 && scrollXSize" class="c-table-fixed-right flex-column" :style="{'bottom': `${scrollXSize}px`, 'right': `${scrollYSize}px`, 'width': `${columnsWidth.right}px`}">
                <c-thead :store="store" fixed="right" :left="fixedRightShifting"></c-thead>
                <c-tbody :store="store" fixed="right" :left="fixedRightShifting"></c-tbody>
            </div>

            <div v-show="scrollYSize" class="c-table-head-scroll-place" :style="{'width': `${scrollYSize}px`, 'height': `${36 * header.length + 1}px`}"></div>
        </div>

        <div v-if="!value && paging" class="c-table-foot flex-between-center">
            <div class="page-size flex-center">
                <span class="">共{{total}}条</span>
                <c-select v-model="pageSize" :options="pageSizeOptions" :default-option="false"></c-select>
            </div>
            <div class="page flex-center">
                <a class="tf tf-prev" :class="{'z-dis': pageIndex <= 1}" @click="update(1)"></a>
                <a class="tf tf-tri-left" :class="{'z-dis': pageIndex <= 1}" @click="update(pageIndex - 1)"></a>
                <span>第</span>
                <input v-model="pageIndex" type="text" @keyup.enter="update(pageIndex)">
                <span>页</span>
                <span>共{{pageCount}}页</span>
                <a class="tf tf-tri-right" :class="{'z-dis': pageIndex >= pageCount}" @click="update(pageIndex + 1)"></a>
                <a class="tf tf-next" :class="{'z-dis': pageIndex >= pageCount}" @click="update(pageCount)"></a>
                <a class="tf tf-refresh" @click="update(pageIndex)"></a>
            </div>
        </div>

        <div style="display:none;"><slot></slot></div>
    </div>
</template>

<script>
import cThead from './thead.js'
import cTbody from './tbody.js'
import {createStore, mapState} from './store/index.js'

export default {
    name: 'cTable',

    components: {
        cThead, cTbody
    },

    props: {
        value: Array,  // 表格数据

        code: String,  // 表格code，用于获取字段

        height: [String, Number],  // 表格高度

        border: Boolean,  // 是否显示边框

        paging: {  // 是否分页
            type: Boolean,
            default: true
        }
    },

    data() {
        return {
            id: `cTableIndex${cTableIndex++}`,
            store: createStore(this),

            pageIndex: 1,
            total: 0,
            pageSize: 20,
            pageSizeOptions: [{
                name: '10条/页',
                value: 10
            }, {
                name: '20条/页',
                value: 20
            }, {
                name: '50条/页',
                value: 50
            }, {
                name: '100条/页',
                value: 100
            }]
        }
    },

    computed: {
        ...mapState(['dataList', 'header', 'columnsWidth', 'scrollXSize', 'scrollYSize']),

        fixedRightShifting() {
            return this.columnsWidth.right - this.columnsWidth.all + 'px';
        },

        pageCount() {
            return Math.ceil(this.total / this.pageSize) || 1;
        }
    },

    watch: {
        dataList(val) {
            this._updateData = true;
            setTimeout(() => {
                this._updateData = false;
            });
            this.$emit('input', Array.from(val, item => item.data));
            this.resize();
        },

        value(val) {
            if (this._updateData) {
                this._updateData = false;
            } else{
                this.store.setData(val);
            }
        },

        pageSize() {
            this.update(1);
        }
    },

    mounted() {
        this.bindEvent();
        this.resize();

        new Promise(resolve => {
            if (this.code) {
                this.request({
                    url: '/system_table_field/get_table_field_list',
                    data: {
                        list_unique_code: this.code,
                        enable_state: 1
                    },
                    success: data => {
                        resolve(data);
                    }
                });
            } else {
                resolve([]);
            }
        }).then(columnPower => {
            var list = [], selector, expand;
            this.$children.filter(item => item.$options.name == 'cTableColumn').forEach(col => {
                if (col.type == 'selector') {
                    selector = col;
                    return;
                } else if (col.type == 'expand') {
                    expand = col;
                    return;
                }

                let index = columnPower.findIndex(item => item.field_code == col.name),
                    power = columnPower[index];

                if (power) {
                    if (power.default_display == 1) {
                        col.powerConfig = {
                            fixed: power.is_locked == 2 ? 'left' : undefined
                        }
                        list.splice(index, 0, col);
                    }
                } else {
                    list.push(col);
                }
            });
            expand && list.unshift(expand);
            selector && list.unshift(selector);
            this.store.commit('columnConfig', list);
        });

        if (this.value) {
            this.store.setData(this.value);
        }

        this.store.commit('rowClick', !!this._events['row-click']);
    },

    activated() {
        this._hidden = false;
        this.resize();
        this.setScroll();
    },

    deactivated() {
        this._hidden = true;
        this.store.commit('scrollX', false);
    },

    beforeDestroy() {
        window.removeEventListener('resize', this._resizeHandler);
    },

    methods: {
        setScroll() {
            var $theadWrapper = document.querySelector('#' + this.id + ' .c-table-main .c-table-head-wrapper'),
                $tbodyWrapper = document.querySelector('#' + this.id + ' .c-table-main .c-table-body-wrapper'),
                $fixedLeftTbodyWrapper = document.querySelector('#' + this.id + ' .c-table-fixed-left .c-table-body-wrapper'),
                $fixedRightTbodyWrapper = document.querySelector('#' + this.id + ' .c-table-fixed-right .c-table-body-wrapper');

            $theadWrapper.scrollLeft = this._scrollLeft || 0;
            $tbodyWrapper.scrollLeft = this._scrollLeft || 0;
            $tbodyWrapper.scrollTop = this._scrollTop || 0;
            $fixedLeftTbodyWrapper.scrollTop = this._scrollTop || 0;
            $fixedRightTbodyWrapper.scrollTop = this._scrollTop || 0;
        },

        bindEvent() {
            var $table = document.getElementById(this.id),
                $theadWrapper = document.querySelector('#' + this.id + ' .c-table-main .c-table-head-wrapper'),
                $tbodyWrapper = document.querySelector('#' + this.id + ' .c-table-main .c-table-body-wrapper'),
                $tbody = document.querySelector('#' + this.id + ' .c-table-main .c-table-body-wrapper table') || document.querySelector('#' + this.id + ' .c-table-main .c-table-body-wrapper .c-table-body-table'),
                $fixedLeftTbodyWrapper = document.querySelector('#' + this.id + ' .c-table-fixed-left .c-table-body-wrapper'),
                $fixedRightTbodyWrapper = document.querySelector('#' + this.id + ' .c-table-fixed-right .c-table-body-wrapper');

            this._resizeHandler = () => {
                if (this._hidden) return;
                this.store.commit('scrollX', $table.clientWidth < this.columnsWidth.all + this.scrollYSize);
                this.store.commit('scrollY', $tbody.clientHeight > $tbodyWrapper.clientHeight);
            }

            window.addEventListener('resize', this._resizeHandler);

            $tbodyWrapper.onscroll = () => {
                $theadWrapper.scrollLeft = $tbodyWrapper.scrollLeft;
                $fixedLeftTbodyWrapper.scrollTop = $tbodyWrapper.scrollTop;
                $fixedRightTbodyWrapper.scrollTop = $tbodyWrapper.scrollTop;
                this._scrollLeft = $tbodyWrapper.scrollLeft;
                this._scrollTop = $tbodyWrapper.scrollTop;
            };
        },

        resize() {
            this.$nextTick(() => {
                this._resizeHandler();
            });
            setTimeout(() => {
                this._resizeHandler();
            }, 100);
        },

        load(opt, page) {
            this.store.setData([]);
            this.opt = opt;
            this.update(page || 1, !!page);
        },

        update(page, specify) {
            if (!this.opt || !this.opt.url) return;

            page = parseInt(page || this.pageIndex) || 1;
            if (!specify) {
                page = page < 1 ? 1 : page > this.pageCount ? this.pageCount : page;
            }

            this.pageIndex = page;
            this.requestObj && this.requestObj.abort();
            let load = this.$loading({
                dom: document.querySelector('#' + this.id)
            });
            this.requestObj = this.request({
                url: this.opt.url,
                data: Object.assign({
                    pageIndex: page,
                    pageSize: this.pageSize
                }, this.opt.data || {}),
                success: data => {
                    try {
                        this.scrollTop();
                    } catch(e) {}
                    data = data || [];
                    if (this.opt.dataFormatter) {
                        data = this.opt.dataFormatter(data);
                    }
                    this.store.setData(data);
                    this.store.commit('emptyState', !data.length);
                },
                response: res => {
                    this.total = res.total;
                    load.close();
                }
            });
        },

        getRow(filter = 'all') {
            if (filter == 'all') {
                return this.dataList;
            } else if (filter == 'selected') {
                return this.getRow(item => item.selected);
            } else if (typeof filter == 'function') {
                let list = [];
                !function poll(arr) {
                    if (!arr) return;
                    arr.forEach((item, index) => {
                        if (filter(item)) {
                            list.push(item);
                        }

                        poll(item.expandData.children);
                    });
                }(this.dataList);
                return list;
            } else {
                let list = [];
                (typeof filter != 'object' ? [filter] : filter).forEach(item => {
                    list.push(this.dataList[item]);
                });
                return typeof filter == 'object' ? list : list[0];
            }
        },

        push(data) {
            this.store.dataList.push(this.store.createRow({
                data
            }));
            this.store.commit('emptyState', false);
        },

        insert(index, data) {
            if (index !== undefined) {
                this.store.dataList.splice(index, 0, this.store.createRow({
                    data
                }));
            }
        },

        scrollTop(top = 0) {
            this.$nextTick(() => {
                try {
                    this._scrollTop = 0;
                    document.querySelector('#' + this.id + ' .c-table-main .c-table-body-wrapper').scrollTop = top;
                } catch(e) {}
            })
        }
    }
};
</script>

<style lang="stylus">
@import './style/index'
</style>